import type {
  ContractDuplicateCheckParams,
  ContractDuplicateCheckResponse,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysContract } from '../queryKeys';

import { fetchContractDuplicateCheck } from './fetchContractDuplicateCheck';

const hasSearchCriteria = (params: ContractDuplicateCheckParams) =>
  Object.values(params).some(Boolean);

/**
 * Generates the query options for the contract duplicate check query so they can be shared
 * between the useQuery hook and prefetching. The query is skipped until at least one search
 * attribute is provided, since an empty request would match every contract.
 *
 * @param params - The attributes to check for existing likely-duplicate contracts.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractDuplicateCheckQueryOptions = <
  TData = ContractDuplicateCheckResponse,
>({
  params,
  options,
}: {
  params: ContractDuplicateCheckParams | undefined;
  options?: Omit<
    UseQueryOptions<ContractDuplicateCheckResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn:
      params && hasSearchCriteria(params)
        ? async ({ signal }: QueryFunctionContext) =>
            fetchContractDuplicateCheck(params, { signal })
        : skipToken,
    queryKey: queryKeysContract.getDuplicateCheck(params),
    staleTime: 0, // Always perform a fresh duplicate check
    ...options,
  });

/**
 * React Query based custom hook for checking whether a contract with similar attributes
 * (contract number, supplier, and/or date range) already exists.
 *
 * @param params - The attributes to check for existing likely-duplicate contracts.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Import-Jobs/operation/duplicate_check_contracts_duplicate_check_post
 */
// fallow-ignore-next-line unused-export -- no UI consumer wired up yet; intended data-layer API surface for a future duplicate-warning feature.
export const useQueryContractDuplicateCheck = (
  params: ContractDuplicateCheckParams | undefined,
  options?: Omit<
    UseQueryOptions<
      ContractDuplicateCheckResponse,
      VestigasHTTPError,
      ContractDuplicateCheckResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContractDuplicateCheckQueryOptions<ContractDuplicateCheckResponse>({
      options,
      params,
    }),
  );
